<template>
  <v-navigation-drawer
    :value="value"
    right
    fixed
    stateless
    hide-overlay
    :width="drawerWidth"
    class="row-details-drawer"
    :class="$vuetify.theme.isDark ? 'theme--dark' : 'theme--light'"
    aria-label="Record details"
    @input="$emit('input', $event)">
    <div class="row-details-drawer__layout">
        <!-- Top Emerald Brand Accent Bar -->
        <div class="row-details-drawer__accent-bar"></div>

        <!-- Header -->
        <header class="row-details-drawer__header">
          <div class="row-details-drawer__heading">
            <div class="row-details-drawer__eyebrow">Record details</div>
            <div class="row-details-drawer__title-row">
              <h2 class="row-details-drawer__title">{{ displayTitle }}</h2>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                   icon
                    class="row-details-drawer__copy-btn"
                    aria-label="Copy record identifier"
                    v-bind="attrs"
                    v-on="on"
                    @click="copyId">
                    <v-icon small>{{ isCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ isCopied ? 'Copied!' : 'Copy identifier' }}</span>
              </v-tooltip>
              <span class="row-details-drawer__sr-status" role="status" aria-live="polite">
                {{ isCopied ? 'Record identifier copied' : '' }}
              </span>
            </div>
            <span v-if="displaySubtitle" class="row-details-drawer__subtitle">
              {{ displaySubtitle }}
            </span>
          </div>
          <v-btn
            ref="closeButton"
            icon
            class="row-details-drawer__close"
            aria-label="Close record details"
            @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-divider class="row-details-drawer__divider"></v-divider>

        <!-- Body -->
        <div class="row-details-drawer__body">
          <!-- Dynamic Status Summary Row (if status property exists) -->
          <div v-if="statusValue" class="row-details-drawer__summary">
            <span class="row-details-drawer__summary-label">Status</span>
            <v-chip
              small
              outlined
              :color="statusColor"
              class="row-details-drawer__status-chip font-weight-medium">
              <span class="row-details-drawer__status-dot"></span>
              {{ statusValue }}
            </v-chip>
          </div>

          <!-- Dynamic Key-Value Fields Grid -->
          <dl v-if="fields.length" class="row-details-drawer__fields">
            <div
              v-for="field in fields"
              :key="field.key"
              class="row-details-drawer__field"
              :class="{
                'row-details-drawer__field--narrative': field.narrative,
                'row-details-drawer__field--mono': field.mono
              }">
              <dt class="row-details-drawer__field-label">{{ field.label }}</dt>
              <dd :class="{ 'row-details-drawer__blank': field.blank }">
                <template v-if="field.isBool && !field.blank">
                  <span class="row-details-drawer__bool-chip" :class="field.value === 'Yes' ? 'row-details-drawer__bool-chip--yes' : 'row-details-drawer__bool-chip--no'">
                    {{ field.value }}
                  </span>
                </template>
                <template v-else>
                  {{ field.value }}
                </template>
              </dd>
            </div>
          </dl>

          <div v-else class="row-details-drawer__empty" role="status">
            <v-icon large color="grey lighten-1" class="mb-2">mdi-database-off-outline</v-icon>
            <div>No record details are available.</div>
          </div>
        </div>

        <!-- View-Only Footer: Isolate in 3D Action -->
        <footer v-if="canIsolate" class="row-details-drawer__footer">
          <v-btn
            outlined
            color="primary"
            class="row-details-drawer__isolate-btn"
            block
            @click="$emit('isolate', row)">
            <v-icon left small>mdi-cube-outline</v-icon>
            {{ row && row.__SpatialActionMode === 'relatedEquipment' ? 'Show related equipment' : 'Show in 3D' }}
          </v-btn>
        </footer>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { formatDateValue, friendlyType, isGuidValue } from '@/features/Dashboard/utils/columnTypes';

const TECHNICAL_FIELDS = new Set(['__viewer', '__rowkey', '__canisolate', '__spatialavailability', '__spatialreason', '__spatialactionmode', 'tagids']);
const TITLE_FIELDS = ['InspectionNo', 'EquipmentNo', 'Title', 'Name', 'TagNo', 'Id', 'Code', 'WorkOrderNo', 'SegmentId'];
const SUBTITLE_FIELDS = ['EquipmentNo', 'TagNo', 'TaskBoardName', 'Category', 'Type', 'System', 'LineNumber', 'Area'];
const NARRATIVE_FIELD = /(recommend|finding|comment|description|note|action|scope|remark|observation|summary|reason)/i;

function columnKey(column) {
  if (typeof column === 'string') return column;
  return column && (column.name || column.value || column.column || column.field);
}

function humanize(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, match => match.toUpperCase());
}

export default {
  name: 'RowDetailsDrawer',
  props: {
    value: { type: Boolean, default: false },
    row: { type: Object, default: null },
    config: { type: Object, default: null },
    canIsolate: { type: Boolean, default: false },
  },
  data() {
    return {
      isCopied: false,
      copyTimeout: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this._previousActiveElement = document.activeElement;
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.$nextTick(() => this.$refs.closeButton?.$el?.focus?.());
      } else {
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this._previousActiveElement?.focus?.();
        this._previousActiveElement = null;
      }
    },
  },
  beforeDestroy() {
    if (this.copyTimeout) clearTimeout(this.copyTimeout);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  computed: {
    drawerWidth() {
      return this.$vuetify?.breakpoint?.smAndDown ? '100%' : 440;
    },
    displayTitle() {
      if (!this.row) return 'Record';
      const key = TITLE_FIELDS.find(field => this.row[field] !== null && this.row[field] !== undefined && this.row[field] !== '');
      if (key) return String(this.row[key]);
      const firstAvailableKey = Object.keys(this.row).find(k => !TECHNICAL_FIELDS.has(k.toLowerCase()) && this.row[k]);
      return firstAvailableKey ? String(this.row[firstAvailableKey]) : 'Record';
    },
    displaySubtitle() {
      if (!this.row) return '';
      const titleValue = this.displayTitle;
      const key = SUBTITLE_FIELDS.find(field => {
        const value = this.row[field];
        return value !== null && value !== undefined && value !== '' && String(value) !== titleValue;
      });
      return key ? `${humanize(key)} · ${this.row[key]}` : '';
    },
    statusKey() {
      if (!this.row) return '';
      return ['statusname', 'status', 'state']
        .map(candidate => Object.keys(this.row).find(key => key.toLowerCase() === candidate))
        .find(Boolean) || '';
    },
    statusValue() {
      const value = this.statusKey ? this.row[this.statusKey] : null;
      return value === null || value === undefined || value === '' ? '' : String(value);
    },
    statusColor() {
      const status = this.statusValue.toLowerCase();
      if (/(reject|block|fail|cancel|overdue)/.test(status)) return 'error';
      if (/(partial|pending|progress|planned|await|raised)/.test(status)) return 'warning';
      if (/(approv|closed|complete|done|service|operational)/.test(status)) return 'primary';
      return undefined;
    },
    fields() {
      if (!this.row) return [];
      const rowKeys = Object.keys(this.row);
      const configured = Array.isArray(this.config?.columns) ? this.config.columns : [];
      const orderedKeys = [];
      const seen = new Set();

      configured.forEach(column => {
        const key = columnKey(column);
        if (!key || seen.has(key) || !rowKeys.includes(key) || this.isTechnicalField(key)) return;
        orderedKeys.push(key);
        seen.add(key);
      });
      rowKeys.forEach(key => {
        if (seen.has(key) || this.isTechnicalField(key)) return;
        orderedKeys.push(key);
        seen.add(key);
      });

      return orderedKeys.map(key => this.fieldFor(key));
    },
  },
  methods: {
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.value) this.$emit('input', false);
    },
    async copyId() {
      const text = this.displayTitle;
      if (!text || !navigator.clipboard?.writeText) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        void err;
        return;
      }
      this.isCopied = true;
      if (this.copyTimeout) clearTimeout(this.copyTimeout);
      this.copyTimeout = setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    },
    isTechnicalField(key) {
      return TECHNICAL_FIELDS.has(String(key || '').toLowerCase()) || key === this.statusKey;
    },
    fieldFor(key) {
      const raw = this.row[key];
      const blank = raw === null || raw === undefined || raw === '' || (typeof raw === 'string' && raw.trim() === '');
      const type = friendlyType(this.config?.columnTypes?.[key]).key;
      const authoredLabel = this.config?.columnDisplayNames?.[key];
      const isBool = type === 'bool' || typeof raw === 'boolean' || (typeof raw === 'string' && (raw.toLowerCase() === 'true' || raw.toLowerCase() === 'false'));
      let value;

      if (blank) {
        value = '—';
      } else if (isBool) {
        value = (raw === true || raw === 'true' || raw === 1) ? 'Yes' : 'No';
      } else if (type === 'date') {
        value = this.formatDisplayDate(raw);
      } else if (typeof raw === 'string' && (/^\d{4}-\d{2}-\d{2}/.test(raw) || /^\d{2}\/\d{2}\/\d{4}/.test(raw))) {
        value = this.formatDisplayDate(raw);
      } else if (type === 'number' && Number.isFinite(Number(raw))) {
        value = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(Number(raw));
      } else if (typeof raw === 'object') {
        try { value = JSON.stringify(raw); } catch (err) { value = String(raw); }
      } else {
        value = String(raw);
      }

      return {
        key,
        label: authoredLabel && authoredLabel !== key ? authoredLabel : humanize(key),
        value,
        blank,
        isBool,
        mono: isGuidValue(value) || /id|guid|code|number/i.test(key),
        narrative: NARRATIVE_FIELD.test(key) || (typeof raw === 'string' && raw.length > 60),
      };
    },
    formatDisplayDate(raw) {
      const formatted = formatDateValue(raw);
      return formatted.endsWith(' 00:00') ? formatted.slice(0, -6) : formatted;
    },
  },
};
</script>

<style scoped>
.row-details-drawer {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  height: 100vh !important;
  height: 100dvh !important;
  z-index: 210 !important;
  border-radius: 0 !important;
  background: var(--v-background-base, #fff);
  box-shadow: -8px 0 28px rgba(2, 26, 26, 0.14);
}

.row-details-drawer__layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: var(--v-text-base, #1b342d);
}

/* Brand Accent Bar */
.row-details-drawer__accent-bar {
  height: 3px;
  background: var(--ds-brand-castleton, #03624c);
  flex-shrink: 0;
}

.row-details-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 16px;
}

.row-details-drawer__heading {
  min-width: 0;
  flex: 1;
}

.row-details-drawer__eyebrow {
  color: rgba(27, 52, 45, 0.74);
  font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.row-details-drawer__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.row-details-drawer__title {
  margin: 0;
  color: inherit;
  font-family: var(--ds-font-family-heading, "Poppins", sans-serif);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.row-details-drawer__copy-btn.v-btn {
  color: rgba(27, 52, 45, 0.68);
  min-width: 44px;
  min-height: 44px;
  transition: color 0.15s ease;
}

.row-details-drawer__copy-btn.v-btn:hover {
  color: var(--ds-brand-castleton, #03624c);
}

.row-details-drawer__subtitle {
  display: block;
  margin-top: 4px;
  color: rgba(27, 52, 45, 0.72);
  font-size: 13px;
  overflow-wrap: anywhere;
}

.row-details-drawer__close.v-btn {
  flex: 0 0 auto;
  min-width: 44px;
  min-height: 44px;
}

.row-details-drawer__divider {
  border-color: rgba(27, 52, 45, 0.08) !important;
}

.row-details-drawer__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
}

.row-details-drawer__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 10px 14px;
  border-radius: 6px;
  background: rgba(3, 98, 76, 0.04);
  border: 1px solid rgba(3, 98, 76, 0.12);
}

.row-details-drawer__summary-label {
  color: rgba(27, 52, 45, 0.7);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.row-details-drawer__status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin-right: 6px;
}

.row-details-drawer__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 18px;
  margin: 0;
}

.row-details-drawer__field {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.row-details-drawer__field dt {
  color: rgba(27, 52, 45, 0.72);
  font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-details-drawer__field dd {
  margin: 0;
  color: inherit;
  font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.45;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.row-details-drawer__field--narrative {
  grid-column: 1 / -1;
  padding: 12px 14px;
  border: 1px solid rgba(27, 52, 45, 0.1);
  border-radius: 6px;
  background: rgba(3, 98, 76, 0.025);
}

.row-details-drawer__field--mono dd {
  font-family: "IBM Plex Mono", Consolas, monospace;
  font-size: 12.5px;
}

.row-details-drawer__bool-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.row-details-drawer__bool-chip--yes {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.row-details-drawer__bool-chip--no {
  background: rgba(100, 116, 139, 0.12);
  color: #475569;
}

.row-details-drawer__blank {
  color: rgba(27, 52, 45, 0.66) !important;
  font-style: italic;
  font-weight: 400;
}

.row-details-drawer__empty {
  padding: 48px 12px;
  color: rgba(27, 52, 45, 0.72);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.row-details-drawer__footer {
  flex: 0 0 auto;
  padding: 14px 20px 18px;
  border-top: 1px solid rgba(27, 52, 45, 0.08);
  background: var(--v-background-base, #fff);
}

.row-details-drawer__isolate-btn {
  min-height: 44px;
  font-weight: 600 !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

/* Dark Theme Overrides */
.theme--dark .row-details-drawer__eyebrow,
.theme--dark .row-details-drawer__subtitle,
.theme--dark .row-details-drawer__summary-label,
.theme--dark .row-details-drawer__field dt,
.theme--dark .row-details-drawer__blank,
.theme--dark .row-details-drawer__empty {
  color: rgba(255, 255, 255, 0.68) !important;
}

.theme--dark .row-details-drawer__summary {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .row-details-drawer__copy-btn.v-btn {
  color: rgba(255, 255, 255, 0.78);
}

.theme--dark .row-details-drawer__bool-chip--yes {
  background: rgba(52, 211, 153, 0.2);
  color: #a7f3d0;
}

.theme--dark .row-details-drawer__bool-chip--no {
  background: rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
}

.theme--dark .row-details-drawer__field--narrative {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
}

.theme--dark .row-details-drawer__divider,
.theme--dark .row-details-drawer__footer {
  border-color: rgba(255, 255, 255, 0.08);
}

.row-details-drawer__sr-status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 600px) {
  .row-details-drawer__fields { grid-template-columns: 1fr; }
  .row-details-drawer__field--narrative { grid-column: auto; }
}
</style>
